import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from './lib/utils';

/**
 * Industrial typography primitives. A "datum" pairs a small-caps tracking
 * label with a tabular monospace value — the precision-instrument treatment
 * used by SelectedRowMenu, the planner summary strip, and the workload
 * matrix cells. The variant functions are exported so class-only consumers
 * (e.g. AG Grid cell renderers) can compose them without mounting elements.
 */
const datumLabelVariants = cva('font-sans uppercase', {
  variants: {
    size: {
      label: 'text-[10px] font-semibold tracking-datum text-foreground/55',
      caption: 'text-[9.5px] font-medium tracking-datum-caption text-foreground/40',
    },
  },
  defaultVariants: { size: 'label' },
});

const datumValueVariants = cva("font-mono tabular-nums [font-feature-settings:'zero'_1]", {
  variants: {
    size: {
      sm: 'text-[12px]',
      md: 'text-base font-semibold',
      lg: 'text-lg font-medium tracking-[-0.01em]',
    },
  },
  defaultVariants: { size: 'lg' },
});

function DatumLabel({
  className,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof datumLabelVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="datum-label"
      className={cn(datumLabelVariants({ size }), className)}
      {...props}
    />
  );
}

function DatumValue({
  className,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof datumValueVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="datum-value"
      className={cn(datumValueVariants({ size }), className)}
      {...props}
    />
  );
}

export { DatumLabel, datumLabelVariants, DatumValue, datumValueVariants };
